import isEqual from 'fast-deep-equal';
import { useEffect, useState } from 'react';

import {
  File,
  DocumentEdge,
  Document,
  useTeamAvatarsThumbnailByIdsLazyQuery,
} from '@graphql/generated';
import { isUrlExpired } from '@hooks/useGetDocumentsByClientIdsQuery';
import { AvatarThumbnailCachePrefix } from '@src/constants/constants';
import {
  buildAvatarThumbnailCacheEntries,
  getAvatarUrlTimestamp,
  setAvatarUrlsWithTimestamp,
  shouldRefetchAvatarUrl,
} from '@utils/avatarUrlCache';
import { AsyncStorage } from '@utils/storage';

export const useGetTeamAvatarsByIdsQuery = () => {
  const [avatarIds, setAvatarIds] = useState<string[]>([]);
  // here use useTeamAvatarsThumbnailByIdsQuery because thumbnail is enough for display,
  // except change profile in my account page.

  // TA-10914 (PR review parity with useGetUserAvatarsByIdsQuery.tsx):
  // `avatarIds` only ever holds ids already confirmed to need refetching
  // (see checkTeamAvatarURLByIds below), so a cache-first read here would
  // only ever be wrong — this must always hit the network for a genuinely
  // fresh signature. Lazy (rather than an auto-executing useQuery) so the
  // explicit call in the effect below is the only trigger; combining
  // network-only with an auto-executing query AND an explicit refetch()
  // call fires every batch twice.
  const [urlRefetch, { data: refetchData, error: refetchError }] =
    useTeamAvatarsThumbnailByIdsLazyQuery({
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    if (avatarIds.length > 0) {
      // .catch() guards against refetch() rejecting (e.g. a network error)
      // going unhandled — this call's result is never awaited/consumed by
      // the caller.
      urlRefetch({ variables: { ids: avatarIds } }).catch((e) =>
        // eslint-disable-next-line no-console
        console.log('Error refetching team avatar thumbnails', e)
      );
    }
  }, [avatarIds, urlRefetch]);

  useEffect(() => {
    if (refetchData?.teamAvatarsByIds) {
      const entries = buildAvatarThumbnailCacheEntries(
        refetchData.teamAvatarsByIds,
        AvatarThumbnailCachePrefix
      );
      // `setAvatarUrlsWithTimestamp` (not a plain AsyncStorage.multiSet of
      // just the url) — writing the url without a matching fresh
      // `__fetchedAt` would leave an OLDER, still-recent timestamp from an
      // earlier genuinely-fresh write in place, and `shouldRefetchAvatarUrl`
      // would then trust this now-stale url as "recently fetched" for the
      // rest of that trust window instead of re-validating it.
      setAvatarUrlsWithTimestamp(entries, Date.now()).catch((e) =>
        // eslint-disable-next-line no-console
        console.log('Error caching team avatar thumbnails', e)
      );
    }
  }, [refetchData]);

  useEffect(() => {
    if (refetchError) {
      console.log(
        'Error fromm useTeamAvatarByIdsThumbnailQuery',
        refetchError.message
      );
    }
  }, [refetchError]);

  const updateTeamAvatarIds = (newIds: string[]) => {
    if (isEqual(avatarIds, newIds)) {
      return;
    }
    setAvatarIds(newIds);
  };

  function isDocumentEdge(edge: DocumentEdge | Document): edge is DocumentEdge {
    return '__typename' in edge && edge.__typename === 'DocumentEdge';
  }

  const checkTeamAvatarURL = async (edges: DocumentEdge[] | Document[]) => {
    if ((edges.length ?? 0) > 0) {
      let docs: Document[];
      if (isDocumentEdge(edges[0])) {
        docs = (edges as DocumentEdge[])
          .map((ed: DocumentEdge) => ed.node)
          .filter((n) => n !== undefined) as Document[];
      } else {
        docs = edges as Document[];
      }
      const avatarIdsNeedsToRefetchUrl = await checkTeamAvatarURL2(docs);
      if ((avatarIdsNeedsToRefetchUrl?.needToRefetch?.length ?? 0) > 0) {
        updateTeamAvatarIds(avatarIdsNeedsToRefetchUrl?.needToRefetch ?? []);
      }
    }
  };

  const checkTeamAvatars = async (avatars: File[]) => {
    const avatarIds = new Set<string>();
    avatars.forEach((m) => {
      if (m?.id) {
        avatarIds.add(m.id);
      }
    });
    checkTeamAvatarURLByIds([...avatarIds]);
  };
  const checkTeamAvatarURLByIds = async (ids: string[]) => {
    if ((ids.length ?? 0) > 0) {
      const avatarIdsNeedsToRefetchUrl = new Set<string>();
      const nowMs = Date.now();
      for (const item of ids) {
        const key = AvatarThumbnailCachePrefix + item;
        const url = await AsyncStorage.getItem(key);
        const fetchedAtMs = await getAvatarUrlTimestamp(key);
        if (shouldRefetchAvatarUrl(url, fetchedAtMs, nowMs)) {
          avatarIdsNeedsToRefetchUrl.add(item);
        }
      }

      updateTeamAvatarIds([...avatarIdsNeedsToRefetchUrl]);
    }
  };

  const checkTeamAvatarIds = async (avatarIds: string[]) => {
    if ((avatarIds.length ?? 0) > 0) {
      updateTeamAvatarIds(avatarIds);
    }
  };

  const checkTeamAvatarURL2 = async (docs: Document[]) => {
    if ((docs.length ?? 0) > 0) {
      const avatarIdsNeedsToRefetchUrl = new Set<string>();
      const avatarIdsWithUrl: { clientId: string; url: string }[] = [];
      const now = new Date();
      for (const item of docs) {
        if (item?.__typename === 'Document' && item.clientId) {
          const url = await AsyncStorage.getItem(item.clientId);
          if (!url) {
            avatarIdsNeedsToRefetchUrl.add(item.clientId);
          } else if (isUrlExpired(url, now)) {
            avatarIdsNeedsToRefetchUrl.add(item.clientId);
          } else {
            avatarIdsWithUrl.push({ clientId: item.clientId, url });
          }
        }
      }

      return {
        needToRefetch: [...avatarIdsNeedsToRefetchUrl],
        urls: avatarIdsWithUrl,
      };
    }
  };

  return {
    checkTeamAvatarURLByIds,
    checkTeamAvatars,
    checkTeamAvatarURL,
    checkTeamAvatarIds,
  };
};
